// src/Pages/Purchase/PurchaseData/ViewExpenses.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../../../config";
import "./ViewExpenses.css";

const ViewExpenses = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExpense = async () => {
      try {
        const res = await fetch(`${API_URL}/api/expenses/${id}`, {
          credentials: "include",
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (data.success && data.data) {
          setExpense(data.data);
        } else {
          alert("Expense not found");
          navigate("/purchase/expenses");
        }
      } catch (err) {
        console.error("Failed to fetch expense:", err);
        alert("Error loading expense");
        navigate("/purchase/expenses");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchExpense();
  }, [id, navigate]);

  if (loading) return <div className="loading">Loading Expense...</div>;
  if (!expense) return <div className="error">Expense not found</div>;

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-IN") : "—";

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: expense.currency || "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);

  return (
    <div className="view-expense-page">
      <div className="expense-print-container">
        {/* Header */}
        <div className="expense-header-section">
          <div className="company-details">
            <h1>Your Company Name</h1>
            <p>123 Business Street, City, State - 400001</p>
            <p>GSTIN: 22AAAAA0000A1Z5 | Email: info@yourcompany.com</p>
          </div>
          <div className="expense-title">
            <h1>EXPENSE</h1>
            <h2>{expense.expenseNo}</h2>
          </div>
        </div>

        {/* Meta Info */}
        <div className="expense-meta-grid">
          <div>
            <strong>Expense Date</strong>
            <br />
            {formatDate(expense.date)}
          </div>
          <div>
            <strong>Paid Through</strong>
            <br />
            {expense.paidThrough || "—"}
          </div>
          <div>
            <strong>Reference</strong>
            <br />
            {expense.referenceNo || "—"}
          </div>
          <div>
            <strong>Status</strong>
            <br />
            <span
              className={`status-badge ${
                expense.status === "PAID"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {expense.status || "UNPAID"}
            </span>
          </div>
        </div>

        {/* Vendor & Customer */}
        <div className="vendor-customer-section">
          <div>
            <strong>Vendor</strong>
            <br />
            <strong>{expense.vendorName || "—"}</strong>
          </div>
          {expense.customerName && (
            <div>
              <strong>Billable To Customer</strong>
              <br />
              <strong>{expense.customerName}</strong>
            </div>
          )}
        </div>

        {/* Items Table */}
        <table className="items-table-print">
          <thead>
            <tr>
              <th>{expense.isItemized ? "ACCOUNT" : "EXPENSE ACCOUNT"}</th>
              <th>{expense.isItemized ? "DESCRIPTION" : "REFERENCE"}</th>
              <th className="text-right">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {expense.isItemized ? (
              expense.items.map((item, i) => (
                <tr key={i}>
                  <td>{item.account || "—"}</td>
                  <td>{item.description || "—"}</td>
                  <td className="text-right">{formatCurrency(item.amount)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td>{expense.items[0]?.account || "—"}</td>
                <td>{expense.referenceNo || expense.invoiceNo || "—"}</td>
                <td className="text-right">{formatCurrency(expense.total)}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Total */}
        <div className="totals-section">
          <div className="totals-left">
            <div className="grand-total">Grand Total</div>
          </div>
          <div className="totals-right">
            <div className="grand-total">{formatCurrency(expense.total)}</div>
          </div>
        </div>

        {/* Notes & Tags */}
        {expense.notes && (
          <div className="notes-section">
            <strong>Notes</strong>
            <div className="mt-2 text-sm whitespace-pre-line">
              {expense.notes}
            </div>
          </div>
        )}

        {expense.reportingTags && expense.reportingTags.length > 0 && (
          <div className="tags-section">
            <strong>Reporting Tags</strong>
            <div className="mt-2 text-sm">
              {expense.reportingTags.join(", ")}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons-print">
          <button onClick={() => window.print()} className="print-btn">
            Print / Save as PDF
          </button>
          <button
            onClick={() => navigate("/purchase/expenses")}
            className="back-btn"
          >
            ← Back to Expenses
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewExpenses;
